
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { BirdieRecord } from '@/types';

export const useBirdieRecords = () => {
  const { user } = useAuth();

  // Use React Query to fetch birdie records with proper error handling
  const { data: birdieRecords = [], isLoading: isLoadingBirdies } = useQuery({
    queryKey: ['birdieRecords', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      try {
        // Get all sub-par scores (birdie, eagle, double_eagle)
        const { data, error } = await supabase
          .from('scores')
          .select('id, user_id, course_id, hole_number, score_date, tee, notes, score_type');
          
        if (error) {
          console.error('Error fetching birdie records:', error);
          throw error;
        }
        
        // Transform the data into BirdieRecord format
        const birdieRecords: BirdieRecord[] = (data || []).map(record => ({
          id: record.id,
          userId: record.user_id,
          courseId: record.course_id,
          holeNumber: record.hole_number,
          date: record.score_date,
          teeBox: record.tee,
          scoreType: record.score_type,
          notes: record.notes || undefined,
          gameIds: [] // We'll populate this separately if needed
        }));
        
        // If we need game associations, we can fetch them separately for each record
        if (birdieRecords.length > 0) {
          const scoreIds = birdieRecords.map(record => record.id);
          
          // Get game associations for these scores
          const { data: gameAssociations, error: gameError } = await supabase
            .from('score_games')
            .select('score_id, game_id')
            .in('score_id', scoreIds);
            
          if (!gameError && gameAssociations) {
            // Update the birdieRecords with their associated games
            const gameMap = new Map<string, string[]>();
            
            gameAssociations.forEach(assoc => {
              const existingGames = gameMap.get(assoc.score_id) || [];
              gameMap.set(assoc.score_id, [...existingGames, assoc.game_id]);
            });
            
            // Populate the gameIds for each record
            birdieRecords.forEach(record => {
              const games = gameMap.get(record.id);
              if (games && games.length > 0) {
                record.gameIds = games;
              }
            });
          }
        }
        
        return birdieRecords;
      } catch (error) {
        console.error('Error in useBirdieRecords:', error);
        return [];
      }
    },
    enabled: !!user // Only run the query when user exists
  });

  return { birdieRecords, isLoadingBirdies };
};
